import { useRouter } from 'next/router'
import { useRef } from 'react'

import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'

import usePagination from 'app/hooks/usePagination'
import { useGetBlogArticles } from 'app/services/Query/blog/blog.query'
import { getArticlesQueryParams } from 'app/utils/helpers/blog.helpers'
import { isOfType } from 'app/utils/helpers/type.helpers'

import { getBlogCategoryMeta } from '../utils/blog.helpers'

const placeholderArticles = [
  { id: '1' },
  { id: '2' },
  { id: '3' }
] as BlogArticleModel[]

export const useBlogView = () => {
  const router = useRouter()
  const articlesPanelRef = useRef<HTMLElement | null>(null)
  const queryClient = useQueryClient()
  const { formatMessage } = useIntl()

  const { isFallback, query } = router
  const queryCategory = query?.category ?? ''
  const category = isOfType.string(queryCategory) ? queryCategory : ''

  const pathname = `/blog/${category}`
  const scrollTo = () => {
    setTimeout(
      () =>
        articlesPanelRef.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        }),
      0
    )
  }

  const { page, setPage } = usePagination({ pathname, scrollTo })

  const params = getArticlesQueryParams({ category, p: page })
  const isEnabled = page > 1

  const {
    data: blog,
    isFetching,
    isPlaceholderData
  } = useGetBlogArticles({
    params,
    options: {
      enabled: isEnabled,
      placeholderData: {
        articles: placeholderArticles,
        pagy: {},
        articles_count: placeholderArticles.length
      }
    }
  })

  const {
    articles,
    pagy,
    articles_count: articlesCount
  } = blog || ({} as BlogPaginationArticlesModel)

  const handleArticleTagClick = (tag: string) =>
    router.push(`/blog/search-by/${tag}`)

  const showPagination = Number(pagy?.pages) > 1
  const hasArticlesInCategory = !!articles && articles.length > 0
  const isEmptyBlog = !isFetching && articlesCount === 0
  const isEmptyCategory = !isFetching && !hasArticlesInCategory
  const isPageLoading = isFallback || isPlaceholderData

  const { metaTitle, metaDescription } = getBlogCategoryMeta({
    categorySlug: category,
    queryClient,
    formatMessage
  })

  return {
    articlesPanelRef,
    articles,
    showPagination,
    hasArticlesInCategory,
    isEmptyBlog,
    isEmptyCategory,
    isPageLoading,
    pagy,
    handleArticleTagClick,
    page,
    setPage,
    metaTitle,
    metaDescription
  }
}
